/**
 * Issue Card Component
 *
 * Compact two-row layout for scanning through production issues quickly.
 * Row 1: Order info, issue type, OOS details, timestamp
 * Row 2: Action buttons
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import {
  PackageX,
  AlertTriangle,
  RotateCcw,
  Archive,
  XCircle,
  ChevronRight,
  Clock,
  FileWarning,
  GitBranch,
  FileEdit,
  UserCheck,
  Mail,
} from 'lucide-react';
import { formatOrderNumber } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ClientOrgChip } from '@/components/ClientOrgChip';
import { useClientOrgLabels } from '@/hooks/useClientOrgLabels';
import { useRole } from '@/hooks/use-role';
import { issueProductRoute } from '../hooks/usePlatenVariantRoute';
import type { ProductionIssue, IssueType, OrderIssueGroup } from '../types';
import { issueTypeConfig, isOOSIssue } from '../types';
import { ResolveDialog } from './ResolveDialog';
import { IssueRowActionsMenu } from './IssueRowActionsMenu';

const iconMap: Record<IssueType, React.ElementType> = {
  OOS_PRE_BATCH: PackageX,
  INVENTORY_ERROR: PackageX,
  PICKING_DEFECTIVE: AlertTriangle,
  REDO_PENDING: RotateCcw,
  STUCK_BIN: Archive,
  PAP_DAMAGED: XCircle,
  PRINT_DEFECT: AlertTriangle,
  PRINT_WRONG_ITEM: AlertTriangle,
  PRINT_ERROR: AlertTriangle,
  PRINT_FILE_ISSUE: FileWarning,
  SPLIT_ORDER: GitBranch,
  ORDER_ITEM_REMOVED: FileEdit,
  ORDER_CHANGED: FileEdit,
  SKU_NOT_FOUND: FileWarning,
};

const borderColorMap: Record<IssueType, string> = {
  OOS_PRE_BATCH: 'border-l-red-500',
  INVENTORY_ERROR: 'border-l-amber-500',
  PICKING_DEFECTIVE: 'border-l-red-500',
  REDO_PENDING: 'border-l-amber-500',
  STUCK_BIN: 'border-l-amber-500',
  PAP_DAMAGED: 'border-l-red-500',
  PRINT_DEFECT: 'border-l-red-500',
  PRINT_WRONG_ITEM: 'border-l-amber-500',
  PRINT_ERROR: 'border-l-red-500',
  PRINT_FILE_ISSUE: 'border-l-orange-500',
  SPLIT_ORDER: 'border-l-purple-500',
  ORDER_ITEM_REMOVED: 'border-l-orange-500',
  ORDER_CHANGED: 'border-l-orange-500',
  SKU_NOT_FOUND: 'border-l-red-500',
};

interface IssueCardProps {
  /** Single issue (used for non-OOS cards like redos, stuck bins) */
  issue?: ProductionIssue;
  /** Grouped OOS issues for an order */
  group?: OrderIssueGroup;
  onResolve?: () => void;
  onViewDetails?: (issueId: string) => void;
  /**
   * BLU-842: the order's prod_order id, so an OOS card can offer the move /
   * split location actions. Only the OOS list passes it — the Redo and Catalog
   * lists share this card and have no location decision to make.
   */
  prodOrderId?: string;
}

export function IssueCard({ issue: singleIssue, group, onResolve, onViewDetails, prodOrderId }: IssueCardProps) {
  const { t } = useTranslation('production');
  const [showResolveDialog, setShowResolveDialog] = useState(false);

  // Derive the display issue from group or single prop
  const issue = group?.primaryIssue ?? singleIssue!;

  // BLU-3PL: cross-org chip when a 3PL owner views a client's issue at a shared
  // location. Own-org rows (single-tenant, or the owner's own issues) show no chip.
  const { isCrossOrg, labelFor } = useClientOrgLabels();
  const rowOrgId = group?.primaryIssue.organization_id ?? issue.organization_id;
  const showClientChip = isCrossOrg(rowOrgId);
  // BLU-1287: who acts on the product, for the resolve dialog's variant
  // set-up step (from the labels and role already held; no request).
  const { isAdmin } = useRole();
  const productRoute = issueProductRoute(
    showClientChip,
    labelFor(rowOrgId) ?? t('issues.printFiles.platenVariant.theClient'),
    isAdmin(),
  );
  const blockedSkus = group?.blockedSkus;
  const hasMultipleSkus = blockedSkus && blockedSkus.length > 1;

  const config = issueTypeConfig[issue.issue_type];
  const Icon = iconMap[issue.issue_type];
  const borderColor = borderColorMap[issue.issue_type];
  const waitingTime = formatDistanceToNow(
    new Date(group?.createdAt ?? issue.created_at),
    { addSuffix: true }
  );

  const isOOS = isOOSIssue(issue.issue_type);
  const isAwaiting = group?.hasAwaitingInventory ?? issue.metadata?.awaiting_inventory === true;
  const isContacted = group?.hasContacted ?? issue.metadata?.cx_notified_at != null;

  const handleCardClick = () => {
    if (isOOS && onViewDetails) {
      onViewDetails(issue.id);
    }
  };

  return (
    <>
      <div
        className={`border-l-4 ${borderColor} rounded-lg border bg-card transition-colors hover:bg-muted/20 ${
          isOOS && onViewDetails ? 'cursor-pointer' : ''
        }`}
        onClick={handleCardClick}
        role={isOOS && onViewDetails ? 'button' : undefined}
        tabIndex={isOOS && onViewDetails ? 0 : undefined}
        onKeyDown={isOOS && onViewDetails ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        } : undefined}
      >
        {/* Row 1: All info on one line */}
        <div className="flex items-center gap-3 px-4 py-3">
          <div className={`flex-shrink-0 rounded-md p-1.5 ${config.color}`}>
            <Icon className="h-4 w-4" aria-hidden="true" />
          </div>

          {/* Order + customer */}
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-semibold whitespace-nowrap">
              {formatOrderNumber(group?.order_name ?? issue.order_name) || 'Unknown Order'}
            </span>
            {(group?.customer_display ?? issue.customer_display) && (
              <span className="text-sm text-muted-foreground truncate">
                {group?.customer_display ?? issue.customer_display}
              </span>
            )}
          </div>

          {/* Badges */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {showClientChip && <ClientOrgChip label={labelFor(rowOrgId)} />}
            <Badge variant="outline" className={`${config.color} text-xs`}>
              {config.label}
            </Badge>
            {issue.issue_type === 'INVENTORY_ERROR' && issue.status === 'OPEN' && (
              <Badge variant="warning" className="gap-1 text-xs">
                <UserCheck className="h-3 w-3" aria-hidden="true" />
                {t('issues.card.manualReview')}
              </Badge>
            )}
            {isAwaiting && (
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 gap-1 text-xs">
                <Clock className="h-3 w-3" aria-hidden="true" />
                {t('issues.card.awaiting')}
              </Badge>
            )}
            {isOOS && isContacted && (
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 gap-1 text-xs">
                <Mail className="h-3 w-3" aria-hidden="true" />
                {t('issues.card.contacted')}
              </Badge>
            )}
          </div>

          {/* OOS detail inline */}
          {isOOS && blockedSkus && blockedSkus.length > 0 ? (
            <div className="flex items-center gap-2 text-sm flex-shrink-0 ml-auto">
              {hasMultipleSkus ? (
                <Badge variant="destructive" className="text-xs">
                  {blockedSkus.length} SKUs
                </Badge>
              ) : (() => {
                const first = blockedSkus[0]!;
                return (
                  <>
                    <span className="text-red-700 font-medium">{first.sku}</span>
                    {first.deficit != null && first.deficit > 0 && (
                      <span className="text-red-600/80">&minus;{first.deficit}</span>
                    )}
                  </>
                );
              })()}
            </div>
          ) : isOOS && issue.blocked_sku ? (
            <div className="flex items-center gap-2 text-sm flex-shrink-0 ml-auto">
              <span className="text-red-700 font-medium">{issue.blocked_sku}</span>
              {issue.deficit != null && issue.deficit > 0 && (
                <span className="text-red-600/80">&minus;{issue.deficit}</span>
              )}
            </div>
          ) : null}

          {/* Timestamp — push to far right when no OOS detail */}
          <span
            className={`flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap flex-shrink-0 ${
              !(isOOS && (blockedSkus?.length || issue.blocked_sku)) ? 'ml-auto' : ''
            }`}
          >
            <Clock className="h-3 w-3" aria-hidden="true" />
            {waitingTime}
          </span>
        </div>

        {/* Multi-SKU detail row */}
        {hasMultipleSkus && (
          <div className="flex flex-wrap gap-1.5 px-4 pb-2 -mt-1">
            {blockedSkus.map((bs) => (
              <span
                key={bs.sku}
                className="inline-flex items-center gap-1 rounded-md bg-red-50 dark:bg-red-950/50 px-2 py-0.5 text-xs font-mono text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800"
              >
                {bs.sku}
                {bs.deficit != null && bs.deficit > 0 && (
                  <span className="text-red-500">&minus;{bs.deficit}</span>
                )}
              </span>
            ))}
          </div>
        )}

        {/* Notes (rare — only when present) */}
        {issue.notes && (
          <div className="px-4 pb-1 -mt-1">
            <p className="text-sm text-muted-foreground">{issue.notes}</p>
          </div>
        )}

        {/* Row 2: Actions */}
        <div className="flex items-center gap-2 px-4 pb-3 pt-1">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={(e) => {
              e.stopPropagation();
              setShowResolveDialog(true);
            }}
          >
            {t('issues.resolve')}
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
          {/* BLU-842: location actions, OOS only. `onViewDetails` is what opens
              the order detail sheet, so Move needs it to exist. */}
          {isOOS && prodOrderId && onViewDetails && (
            <IssueRowActionsMenu
              prodOrderId={prodOrderId}
              from="oos"
              onMove={() => onViewDetails(issue.id)}
            />
          )}
        </div>
      </div>

      {/* Resolve Dialog */}
      <ResolveDialog
        open={showResolveDialog}
        onOpenChange={setShowResolveDialog}
        issueId={issue.id}
        locationId={issue.location_id}
        orderName={group?.order_name ?? issue.order_name}
        issueType={issue.issue_type}
        metadata={issue.metadata}
        bridgeFetchState={issue.bridgeFetchState}
        bridgeFetchStateAt={issue.bridgeFetchStateAt}
        bridgeFetchFailReason={issue.bridgeFetchFailReason}
        productRoute={productRoute}
        onSuccess={onResolve}
      />
    </>
  );
}
